/**
 * Duration Utilities - Helper functions for duration formatting operations
 * 
 * Handles formatting of outage duration from "minutes:seconds" format to various display formats
 * 
 * @author CTS - Ford PM UI Team
 */

/**
 * Format types enum
 */
export const FORMAT_TYPES = {
  VERBOSE: 'verbose',    // "3 days 23 hrs 26 min 2 sec"
  COMPACT: 'compact'     // "3:23:26:2"
};

/**
 * Private helper function to format duration in verbose format
 * @private
 */
const _formatVerbose = (days, hours, minutes, seconds) => {
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0) parts.push(`${seconds}s`);

  return parts.length > 0 ? parts.join(' ') : "0 sec";
};

/**
 * Private helper function to format duration in compact format
 * @private
 */
const _formatCompact = (days, hours, minutes, seconds) => {
  return `${days}:${hours}:${minutes}:${seconds}`;
};

/**
 * Formats outage duration from "min : ss" format to specified display format
 * 
 * @param {string} durationString - Duration in "minutes : seconds" format (e.g., "5726 : 2")
 * @param {string} formatType - Format type: 'verbose' or 'compact'
 * @returns {string} Formatted duration string
 * 
 * @example
 * // Verbose format
 * formatOutageDuration("5726 : 2", "verbose")
 * // Returns: "3d 23h 26m 2s"
 * 
 * @example
 * // Compact format
 * formatOutageDuration("5726 : 2", "compact")
 * // Returns: "3:23:26:2"
 */
export const formatOutageDuration = (durationString, formatType = FORMAT_TYPES.COMPACT) => {
  // Validate inputs
  if (!durationString || typeof durationString !== 'string') {
    return formatType === FORMAT_TYPES.VERBOSE ? "0 sec" : "0";
  }

  if (!Object.values(FORMAT_TYPES).includes(formatType)) {
    throw new Error(`Invalid format type. Use '${FORMAT_TYPES.VERBOSE}' or '${FORMAT_TYPES.COMPACT}'`);
  }

  // Parse input - handle both "5726 : 2" format and plain "5726" format
  const parts = durationString.trim().split(':');
  let totalMinutes = 0;
  let seconds = 0;
  
  if (parts.length === 2) {
    // Format: "5726 : 2" (minutes : seconds)
    totalMinutes = Number.parseInt(parts[0].trim(), 10) || 0;
    seconds = Number.parseInt(parts[1].trim(), 10) || 0;
  } else if (parts.length === 1) {
    // Format: "5726" (just minutes) or "5726.5" (minutes with decimal)
    const parsed = Number.parseFloat(parts[0].trim());
    if (!Number.isNaN(parsed)) {
      totalMinutes = Math.floor(parsed);
      seconds = Math.round((parsed - totalMinutes) * 60);
    }
  }
  
  // Calculate days, hours, minutes
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  
  // Check if all values are zero
  if (days === 0 && hours === 0 && minutes === 0 && seconds === 0) {
    return "0";
  }
  
  // Return based on format type
  if (formatType === FORMAT_TYPES.VERBOSE) {
    return _formatVerbose(days, hours, minutes, seconds);
  } else {
    return _formatCompact(days, hours, minutes, seconds);
  }
};


/**
 * Parse outage duration from "min : ss" format to total minutes (for calculations)
 * 
 * @param {string} durationString - Duration in "minutes : seconds" format
 * @returns {number} Total duration in minutes (including fractional minutes from seconds)
 */
export const parseOutageDurationToMinutes = (durationString) => {
  if (!durationString || typeof durationString !== 'string') {
    return 0;
  }
  
  // Parse "5726 : 2" format to total minutes
  const parts = durationString.trim().split(':');
  if (parts.length === 2) {
    const minutes = Number.parseInt(parts[0].trim(), 10) || 0;
    const seconds = Number.parseInt(parts[1].trim(), 10) || 0;
    // Convert to total minutes (including fractional minutes from seconds)
    return minutes + (seconds / 60);
  }
  
  // Fallback: try to parse as number
  const parsed = Number.parseFloat(durationString);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Convert total minutes to "days:hrs:min:ss" format for KPI display
 * 
 * @param {number} totalMinutes - Total minutes (can include fractional minutes)
 * @returns {string} Formatted duration string in "days:hrs:min:ss" format
 * 
 * @example
 * formatMinutesToDuration(5726.033)
 * // Returns: "3:23:26:2"
 */
export const formatMinutesToDuration = (totalMinutes) => {
  if (!totalMinutes || typeof totalMinutes !== 'number' || totalMinutes < 0) {
    return "0";
  }
  
  // Convert fractional minutes to total seconds
  const totalSeconds = Math.floor(totalMinutes * 60);
  
  // Calculate days, hours, minutes, seconds
  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;
  
  // Check if all values are zero
  if (days === 0 && hours === 0 && minutes === 0 && seconds === 0) {
    return "0";
  }
  
  return _formatVerbose(days, hours, minutes, seconds);
};

/**
 * Default export for convenience
 */
const durationUtils = {
  FORMAT_TYPES,
  formatOutageDuration,
  parseOutageDurationToMinutes,
  formatMinutesToDuration
};

export default durationUtils;
